// Issue #178: one browser call through the dashboard to the #136 calling bridge.
//
// This follows the accepted #136 browser page's dial path: the caller's own
// browser holds the microphone and the WebRTC peer, sends a complete offer,
// then polls for the Echo's answer. Here every request goes to this caller's
// own call on the dashboard, which relays it; the browser never sees the
// bridge or its call reference.

export type BridgeCallStage = "microphone" | "calling" | "ringing" | "connected" | "unknown";
export type BridgeCallProblem = "microphone-blocked" | "camera-blocked" | "camera-missing" | "lost-contact" | "failed";
export type BridgeCallMode = "voice" | "video";
export type BridgeCallRecord = { callId: number; status: string; [key: string]: unknown };

export type BridgeCallHandlers = {
  onStage: (stage: BridgeCallStage) => void;
  onSoundBlocked: (blocked: boolean) => void;
  // Issue #179 prototype: whether the resident's picture is arriving.
  onRemoteVideo?: (showing: boolean) => void;
  onFinished: (result: { call: BridgeCallRecord | null; problem: BridgeCallProblem | null }) => void;
};

type PeerLike = Pick<RTCPeerConnection, "addTrack" | "createOffer" | "createAnswer" | "setLocalDescription" | "setRemoteDescription" | "close" | "addEventListener" | "removeEventListener"> & {
  localDescription: RTCSessionDescription | null;
  iceGatheringState: RTCIceGatheringState;
  signalingState: RTCSignalingState;
  ontrack: ((event: RTCTrackEvent) => void) | null;
  getTransceivers?: () => RTCRtpTransceiver[];
  getStats?: () => Promise<RTCStatsReport>;
};

export type BridgeCallDeps = {
  fetch: typeof fetch;
  getUserMedia: (constraints: MediaStreamConstraints) => Promise<MediaStream>;
  createPeer: (config: RTCConfiguration) => PeerLike;
  window: Pick<Window, "addEventListener" | "removeEventListener" | "setInterval" | "clearInterval" | "setTimeout" | "clearTimeout">;
  now: () => number;
  pollMs: number;
  heartbeatMs: number;
  iceTimeoutMs: number;
  lostAfterMs: number;
  videoCodecs: () => RTCRtpCodec[];
};

const browserDeps = (): BridgeCallDeps => ({
  fetch: (...args) => fetch(...args),
  getUserMedia: constraints => navigator.mediaDevices.getUserMedia(constraints),
  createPeer: config => new RTCPeerConnection(config),
  window,
  now: () => Date.now(),
  pollMs: 1000,
  heartbeatMs: 3000,
  // Amazon prohibits trickle ICE, so only a complete offer is sent. 60 seconds
  // is the #136 tested gathering deadline; a PC with several network adapters
  // took about 40 seconds to finish on 19 October 2026.
  iceTimeoutMs: 60000,
  lostAfterMs: 15000,
  videoCodecs: () => (typeof RTCRtpReceiver !== "undefined" && RTCRtpReceiver.getCapabilities?.("video")?.codecs) || [],
});

// Amazon's published video codecs: VP8 and H.264 constrained baseline only.
export function echoVideoCodecs(codecs: RTCRtpCodec[]) {
  return codecs.filter(codec => codec.mimeType === "video/VP8"
    || (codec.mimeType === "video/H264" && /profile-level-id=42e0/i.test(codec.sdpFmtpLine || "")));
}

export function startBridgeCall({ base, iceServers, audio, mode = "voice", video = null, handlers, deps: overrides = {} }: {
  base: string;
  iceServers: RTCIceServer[];
  audio: HTMLAudioElement | null;
  mode?: BridgeCallMode;
  video?: { remote: HTMLVideoElement | null; local: HTMLVideoElement | null } | null;
  handlers: BridgeCallHandlers;
  deps?: Partial<BridgeCallDeps>;
}) {
  const deps = { ...browserDeps(), ...overrides };
  const isVideo = mode === "video";
  // A video call plays the resident's sound through their picture.
  const player: HTMLMediaElement | null = isVideo ? video?.remote ?? null : audio;
  let stream: MediaStream | null = null, peer: PeerLike | null = null;
  let finished = false, cancelling = false, polling = false, appliedRevision = -1;
  let pollTimer: number | undefined, heartbeatTimer: number | undefined, pictureTimer: number | undefined, lastContact = deps.now();
  // Issue #179 prototype: the resident's picture counts as showing only while new
  // frames arrive. A stopped camera need not mute the track, so frames are counted.
  let framesSeen = -1, stillChecks = 0, pictureShowing: boolean | null = null;
  const reportPicture = (showing: boolean) => { if (showing !== pictureShowing) { pictureShowing = showing; handlers.onRemoteVideo?.(showing); } };
  const checkPicture = async () => {
    const pc = peer;
    if (!pc?.getStats) return;
    let frames = 0;
    try { (await pc.getStats()).forEach(stat => { if (stat.type === "inbound-rtp" && stat.kind === "video") frames += Number(stat.framesDecoded) || 0; }); } catch { return; }
    if (pc !== peer) return;
    if (frames > framesSeen) { framesSeen = frames; stillChecks = 0; if (frames > 0) reportPicture(true); }
    else if (++stillChecks >= 2) reportPicture(false);
  };

  const post = (route: string, body: unknown = {}) => deps.fetch(`${base}/${route}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  const readCall = async (response: Response) => {
    try { const result = await response.json(); return (result?.call ?? null) as BridgeCallRecord | null; } catch { return null; }
  };
  const stopMedia = () => {
    deps.window.clearInterval(pollTimer); deps.window.clearInterval(heartbeatTimer); deps.window.clearInterval(pictureTimer);
    peer?.close(); stream?.getTracks().forEach(track => track.stop());
    peer = null; stream = null;
    if (audio) audio.srcObject = null;
    if (video?.remote) video.remote.srcObject = null;
    if (video?.local) video.local.srcObject = null;
  };
  const leavePage = () => { void deps.fetch(`${base}/cancel`, { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}", keepalive: true }).catch(() => {}); stopMedia(); };
  const finish = (call: BridgeCallRecord | null, problem: BridgeCallProblem | null) => {
    if (finished) return;
    finished = true; stopMedia();
    deps.window.removeEventListener("pagehide", leavePage);
    handlers.onSoundBlocked(false);
    if (isVideo) reportPicture(false);
    handlers.onFinished({ call, problem });
  };
  // Ends the call on the server and reports whatever outcome it recorded.
  const endOnServer = async (problem: BridgeCallProblem | null) => {
    let call: BridgeCallRecord | null = null;
    try { call = await readCall(await post("cancel")); } catch { call = null; }
    finish(call, problem);
  };

  const completeIce = (pc: PeerLike) => new Promise<void>((resolve, reject) => {
    if (pc.iceGatheringState === "complete") return resolve();
    const changed = () => { if (pc.iceGatheringState === "complete") { cleanup(); resolve(); } };
    const timeout = deps.window.setTimeout(() => { cleanup(); reject(new Error("ice-timeout")); }, deps.iceTimeoutMs);
    const cleanup = () => { deps.window.clearTimeout(timeout); pc.removeEventListener("icegatheringstatechange", changed); };
    pc.addEventListener("icegatheringstatechange", changed);
  });

  const playSound = async () => {
    if (!player) return;
    try { await player.play(); handlers.onSoundBlocked(false); } catch { handlers.onSoundBlocked(true); }
  };

  // Applies a new answer from the Echo, or answers its hold/resume update.
  const applyDescription = async (description: { type: "offer" | "answer"; sdp: string; revision: number } | null) => {
    const pc = peer;
    if (!pc || !description || description.revision === appliedRevision) return;
    if (description.type === "answer") {
      if (pc.signalingState === "have-local-offer") await pc.setRemoteDescription({ type: "answer", sdp: description.sdp });
      appliedRevision = description.revision;
    } else {
      // Issue #1581: answer each re-offer (for example the Echo turning its video
      // off) once. The revision is claimed before sending, so a failed or lost reply
      // is never answered again blind; polling then reports what the bridge did.
      appliedRevision = description.revision;
      await pc.setRemoteDescription({ type: "offer", sdp: description.sdp });
      await pc.setLocalDescription(await pc.createAnswer());
      await completeIce(pc);
      if (pc !== peer || !pc.localDescription) return;
      let sent = false;
      try { sent = (await post("reanswer", { revision: description.revision, sdp: pc.localDescription.sdp })).ok; } catch { sent = false; }
      if (!sent && pc === peer && !finished) handlers.onStage("unknown");
    }
  };

  const poll = async () => {
    if (polling || finished || cancelling) return;
    polling = true;
    try {
      const response = await deps.fetch(`${base}/media`);
      const result = await response.json();
      if (!response.ok) throw new Error("media");
      lastContact = deps.now();
      if (result.call) return finish(result.call, null);
      handlers.onStage(result.stage === "unknown" ? "unknown" : result.stage);
      // Count the resident's frames only once connected, so ringing is never "camera off".
      if (isVideo && result.stage === "connected" && pictureTimer === undefined) pictureTimer = deps.window.setInterval(() => void checkPicture(), 1000);
      await applyDescription(result.description ?? null);
    } catch {
      if (deps.now() - lastContact > deps.lostAfterMs && !finished && !cancelling) { cancelling = true; await endOnServer("lost-contact"); }
      else if (!finished) handlers.onStage("unknown");
    } finally { polling = false; }
  };

  const run = async () => {
    deps.window.addEventListener("pagehide", leavePage);
    handlers.onStage("microphone");
    try {
      stream = await deps.getUserMedia({ audio: true, video: isVideo });
    } catch (error) {
      const name = error instanceof Error ? error.name : "";
      const blocked = ["NotAllowedError", "SecurityError"].includes(name);
      if (isVideo && blocked) return endOnServer("camera-blocked");
      if (isVideo && ["NotFoundError", "OverconstrainedError", "NotReadableError"].includes(name)) return endOnServer("camera-missing");
      return endOnServer(blocked ? "microphone-blocked" : "failed");
    }
    if (isVideo && video?.local) video.local.srcObject = stream;
    if (cancelling || finished) { stopMedia(); return; }
    try {
      const pc = deps.createPeer({ iceServers });
      peer = pc;
      stream.getTracks().forEach(track => pc.addTrack(track, stream!));
      if (isVideo) {
        const allowed = echoVideoCodecs(deps.videoCodecs());
        for (const transceiver of pc.getTransceivers?.() ?? []) {
          if (transceiver.sender.track?.kind === "video" && allowed.length) {
            try { transceiver.setCodecPreferences(allowed); } catch { /* the browser keeps its own order */ }
          }
        }
      }
      pc.ontrack = event => {
        if (pc !== peer || !player) return;
        const current = player.srcObject as MediaStream | null;
        if (!isVideo || !current) player.srcObject = event.streams[0] || new MediaStream([event.track]);
        else if (!current.getTracks().includes(event.track)) current.addTrack(event.track);
        void playSound();
      };
      await pc.setLocalDescription(await pc.createOffer());
      await completeIce(pc);
      if (cancelling || finished || pc !== peer || !pc.localDescription) return;
      handlers.onStage("calling");
      const response = await post("dial", { sdp: pc.localDescription.sdp });
      const call = await readCall(response);
      // A dial the bridge refused, or that lost its reply, is already recorded.
      if (call) return finish(call, null);
      if (!response.ok) return endOnServer("failed");
      lastContact = deps.now();
      pollTimer = deps.window.setInterval(() => void poll(), deps.pollMs);
      heartbeatTimer = deps.window.setInterval(() => void post("heartbeat").catch(() => {}), deps.heartbeatMs);
    } catch {
      if (!finished && !cancelling) { cancelling = true; await endOnServer("failed"); }
    }
  };
  void run();

  return {
    // Cancel before an answer, or end the call after one.
    async cancel() {
      if (finished || cancelling) return;
      cancelling = true;
      stopMedia();
      await endOnServer(null);
    },
    playSound,
    // Issue #1581: the care worker's microphone. Muted sends silence; the call carries on.
    setMicrophone(on: boolean) {
      const tracks = stream?.getAudioTracks?.() ?? [];
      tracks.forEach(track => { track.enabled = on; });
      return tracks.length > 0;
    },
    // Issue #179: the care worker's own camera. Off sends no picture; the call carries on.
    setCamera(on: boolean) {
      const tracks = stream?.getVideoTracks?.() ?? [];
      tracks.forEach(track => { track.enabled = on; });
      return tracks.length > 0;
    },
  };
}
